import { Injectable, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ListFormConfig } from '../util/ListForm.config';
import { IEntity } from './AppInterfaceAndBaseClasses';
import { ControllerBase } from './ControllerBase';
import { EntityDataServiceBase } from './EntityDataServiceBase';
import { PaginatorHandler } from './Pagination';
import { UrlQueryDef } from './UrlQueryDef';

/**
    * Base Controller Class for List Forms
    *
    */
@Injectable()
export class ListControllerBase<TDataService extends EntityDataServiceBase> extends ControllerBase
{
  //Component settings
  private loadDataOnInitialize: boolean = true;

  // ********************************************
  // Paging
  // ********************************************

  public pagination: PaginatorHandler<any>;
  public isPagingActive: boolean;

  // ********************************************************************
  // #region Properties
  // ********************************************************************


  /*
  * Rows of data returned from the data service to populate the list
  */
  public dataRows: IEntity[];
  public dataRowsCopy: IEntity[];
  public totalRows: any;

  // *****************************************************
  // Properties for View Binding
  // *****************************************************

  /** This is used so that the crud component can be bound to the list component's selectedId */
  public selectedId: number;

  public showExportButton: boolean;

  public showDeletedButton: boolean;

  public showRefreshButton: boolean;

  public showList: boolean;

  public showCrud: boolean;

  // ********************************************
  // Crud Support Binding Options
  // ********************************************

  public showAddButton: boolean;

  /**
      * If true, the "show deleted rows" prompt is shown
      */
  public showDeletedRowsPrompt: boolean;

  /**
      * Current value of user selection to "include deleted rows"
      */
  public includeDeletedRows: boolean;

  // #endregion

  // ********************************************************************
  // #region Constructor and Related
  // ********************************************************************

  constructor(pageTitle: string = "",
    public titleService: Title,
    protected dataService: TDataService,
    @Optional() listFormConfig?: ListFormConfig)
  {
    super();

    this.pageTitle = pageTitle;

    if (listFormConfig)
    {
      this.loadDataOnInitialize = listFormConfig.loadDataOnInitialize;
    }
  }

  public ngOnInit()
  {
    super.ngOnInit();

    this.setListVisible();

    // Buttons
    this.showExportButton = true;
    this.showRefreshButton = true;

    //flags
    this.includeDeletedRows = false;

    //paging
    this.isPagingActive = true;

    // Crud Buttons
    this.showAddButton = true;

    //Subscriptions
    this.onRegisterEventListeners();

    if (this.loadDataOnInitialize)
      this.getAll();

    this.onAfterInit();
  }

  // #endregion

  // ********************************************************************
  // #region Hook Methods
  // ********************************************************************

  protected onBeforeInit(): void { }
  protected onAfterInit(): void { }

  protected onBeforeGetAll(): void { }
  protected onAfterGetAll(): void { }

  protected onGetQueryDefForGetAll(queryDef: UrlQueryDef): void { }

  // #endregion

  // ********************************************************************
  // #region Event Pub / Sub Methods
  // ********************************************************************

  protected publishListItemSelected(entity: IEntity): void
  {
    // The selectedId is used by the crud component (bound to it's "id" property)
    this.selectedId = entity.id;

    // This is invoked for component bindings.  the onEntitySelected(entity) "event binding" is fired on this component when this is invoked.
    this.onEntitySelected({ selectedEntity: entity });
  }

  private onEntitySelected({ selectedEntity: IEntity }): void { }

  protected publishCrudAddModeRequest(): void
  {
    // The selectedId is used by the crud component (bound to it's "id" property).
    //  When set to a negative value, it indicates that it should go into add mode
    this.selectedId = -1;
  }

  // #endregion

  // ********************************************
  // #region Sub (listeners)
  // ********************************************

  protected onRegisterEventListeners(): void
  {
    if (this.isPagingActive)
    {
      //Pagination
      this.pagination = new PaginatorHandler<any>((pageNumber, pageSize) =>
      {
        return this.dataService
          .get(this.getQueryDefForGetAll(), pageSize, pageNumber);
      });
    }
  }

  /**
  * This method is bound to on the crud component - to allow the crud component to notify the list component when it is done.
  */
  public onCrudComplete(alertMessage: string)
  {
    this.refreshList();

    if (alertMessage) { this.setInfoAlert(alertMessage); }
    else { this.clearAlert(); }

    // Show / hide List and Crud - based on behavior type
    this.setListVisible();
  }


  // #endregion

  // ********************************************************************
  // #region Core Methods
  // ********************************************************************

  public getAll(): void
  {
    this.onBeforeGetAll();

    if (this.isPagingActive)
    {
      this.updatePaginationData();
      return;
    }

    this.dataService.get(this.getQueryDefForGetAll())
      .subscribe((response: any) =>
      {
        //Check if we have a paged result
        if (response.rows != undefined && response.total != undefined)
        {
          this.dataRows = response.rows;
          this.totalRows = response.total;
        }
        else
        {
          this.dataRows = response;
        }

        this.dataRowsCopy = this.dataRows;
        this.onAfterGetAll();
      });
  }

  protected getQueryDefForGetAll(): UrlQueryDef
  {
    var queryDef = new UrlQueryDef();
    queryDef.includeDeletedRows = this.includeDeletedRows;

    this.onGetQueryDefForGetAll(queryDef);

    return queryDef;
  }

  public displayDeletedRows()
  {
    this.includeDeletedRows = !this.includeDeletedRows;
    this.refreshList();
  }

  public refreshList(clearAlert: boolean = true): void
  {
    this.clearAlert(clearAlert);
    this.getAll();
  }

  public export(): void
  {
  }

  // #endregion

  // ********************************************************************
  // #region Crud Support Methods
  // ********************************************************************

  public itemSelected(entity: IEntity): void
  {
    this.publishListItemSelected(entity);

    // Show / hide List and Crud - based on behavior type
    this.setCrudVisible();
  }

  public add(): void
  {
    this.publishCrudAddModeRequest();

    // Show / hide List and Crud - based on behavior type
    this.setCrudVisible();
  }
  // #endregion

  // ********************************************************************
  // #region Crud / List Visibility Methods
  // ********************************************************************

  protected setListVisible(): void
  {
    this.showList = true;

    this.showCrud = false;
  }

  protected setCrudVisible(): void
  {
    this.showCrud = true;

    this.showList = false;
  }

  // #endregion

  // ********************************************************************************
  // #region Paging
  // ********************************************************************************

  protected disableDefaultPaging(): void
  {
    this.isPagingActive = false;
  }

  protected updatePaginationData(): void
  {
    if (this.isPagingActive)
    {
      this.pagination.getPage(this.pagination.pageNumber, this.pagination.pageSize)
        .subscribe(response =>
        {
          this.onAfterGetAll();
        });
    }
  }
  // #endregion Paging

}
